"use client";
import { useForm } from "react-hook-form";
import {
  GetPlaceResponse,
  SearchSuggesetion,
} from "cy-api/aws/location/models";
import { Button, FormError, FormGroup, Input, Label } from "cy-fe";
import { Nationality } from "cy-api/simple-payments/ref-data/models";
import * as StepWizard from "cy-ui/components/StepWizard";
import { BodyContainer } from "cy-remittance/components/layouts";
import { AddressInputs, Country } from "./models";
import { AddressFinder } from "./AddressFinder";
import { NationalitySelect } from "./NationalitySelect";
import { HeaderText } from "../components/HeaderText";

export function AddressPage({
  initial,
  country,
  onSubmit,
  nationalities,
  backUrl,
}: {
  initial: Partial<AddressInputs>;
  country: Country;
  nationalities: Nationality[];
  backUrl: string;
  onSubmit: (inputs: AddressInputs) => Promise<void>;
}) {
  const form = useForm<AddressInputs>({ defaultValues: initial });

  async function handleAddressSelection(suggestion: SearchSuggesetion | null) {
    if (suggestion) {
      const resp = await fetch(
        `/api/public/address-finder/places/${suggestion.PlaceId}`,
      );
      const json = GetPlaceResponse.parse(await resp.json());
      const place = json.Place;
      form.setValue(
        "physicalAddress.address_line_1",
        `${json.Place.AddressNumber} ${place.Street.replaceAll(/  +/g, " ")}`,
      );
      form.setValue("physicalAddress.province", place.Region);
      form.setValue("physicalAddress.postcode", place.PostalCode);
      form.setValue("physicalAddress.city", place.Municipality);
      form.setValue(
        "physicalAddress.suburb",
        place.Neighborhood
          ? place.Neighborhood
          : place.SubMunicipality &&
              place.Municipality !== place.SubMunicipality
            ? place.SubMunicipality
            : "",
      );
    }
  }
  async function handleFormSubmit(values: AddressInputs) {
    await onSubmit(values);
  }
  return (
    <StepWizard.StepPageLayout
      backUrl={backUrl}
      headerRender={() => <HeaderText>Identification Information</HeaderText>}
      subHeaderRender={() => "Tells us about your location of residence"}
    >
      <BodyContainer className="mt-10">
        <AddressFinder country={country} onChange={handleAddressSelection} />

        <form
          className="w-full space-y-5"
          onSubmit={form.handleSubmit(handleFormSubmit)}
        >
          <FormGroup
            controller={{
              control: form.control,
              name: "physicalAddress.address_line_1",
            }}
          >
            <Label>Street</Label>
            <Input
              tabIndex={0}
              vSize="lg"
              {...form.register("physicalAddress.address_line_1", {
                required: { value: true, message: "required" },
              })}
            />
            <FormError />
          </FormGroup>
          <FormGroup
            controller={{
              control: form.control,
              name: "physicalAddress.province",
            }}
          >
            <Label>Province / Region</Label>
            <Input
              tabIndex={0}
              vSize="lg"
              {...form.register("physicalAddress.province", {
                required: { value: true, message: "required" },
              })}
            />
            <FormError />
          </FormGroup>
          <FormGroup
            controller={{ control: form.control, name: "physicalAddress.city" }}
          >
            <Label>Town/City</Label>
            <Input
              tabIndex={0}
              vSize="lg"
              {...form.register("physicalAddress.city", {
                required: { value: true, message: "required" },
              })}
            />
            <FormError />
          </FormGroup>
          <FormGroup
            controller={{
              control: form.control,
              name: "physicalAddress.suburb",
            }}
          >
            <Label>Suburb</Label>
            <Input
              tabIndex={0}
              vSize="lg"
              {...form.register("physicalAddress.suburb", {})}
            />
            <FormError />
          </FormGroup>
          <FormGroup
            controller={{
              control: form.control,
              name: "physicalAddress.postcode",
            }}
          >
            <Label>Postcode / Zip</Label>
            <Input
              tabIndex={0}
              vSize="lg"
              {...form.register("physicalAddress.postcode", {
                required: { value: true, message: "required" },
              })}
            />
            <FormError />
          </FormGroup>
          <FormGroup
            controller={{ control: form.control, name: "nationality" }}
          >
            <Label>Nationality</Label>
            <NationalitySelect form={form} nationalities={nationalities} />
            <FormError />
          </FormGroup>
          <Button
            className="!mt-10 w-full"
            loading={form.formState.isSubmitting}
            vSize="lg"
          >
            Submit
          </Button>
        </form>
      </BodyContainer>
    </StepWizard.StepPageLayout>
  );
}
